import React, { useMemo, useState } from 'react'
import { DateRangePicker } from 'rsuite'
import { BlobProvider } from '@react-pdf/renderer'
import Spread from './PDF/Spread'
import axios from 'axios'
import { getSkuLocation, getCustomers } from 'src/services'
import * as XLSX from 'xlsx'

function SpreadReport() {
  const [dateRange, setDateRange] = useState(null)
  const [error, setError] = useState('')
  const [loadingDate, setLoadingDate] = useState(false)
  const [pdfData, setPDFDate] = useState(null)
  const [map_data, setMapData] = useState({})
  const [additionalLineItems, setAdditionalLineItems] = useState([])
  const [excelPreparing, setExcelPreparing] = useState(false)

  const PDF_MAX_DAYS = 90
  const FETCH_MAX_DAYS = 1000

  const getRangeDays = (range) => {
    if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) return 0
    const start = new Date(range[0])
    const end = new Date(range[1])
    const msPerDay = 1000 * 60 * 60 * 24
    return Math.ceil(Math.abs(end - start) / msPerDay) + 1
  }

  const selectedDays = getRangeDays(dateRange)
  const isLargeRangeForPdf = selectedDays > PDF_MAX_DAYS

  const productLookup = useMemo(() => {
    const map = new Map()
    ;(map_data.product || []).forEach((item) => {
      if (item?.id != null) {
        map.set(Number(item.id), item)
      }
    })
    return map
  }, [map_data.product])

  const mixLookup = useMemo(() => {
    const map = new Map()
    ;(map_data.order_list_products || []).forEach((item) => {
      if (item?.order_id != null && item?.product_mix_id != null) {
        map.set(`${Number(item.order_id)}_${Number(item.product_mix_id)}`, item)
      }
    })
    return map
  }, [map_data.order_list_products])

  const zoneLookup = useMemo(() => {
    const map = new Map()
    ;(map_data.dzone || []).forEach((item) => {
      if (item?.id != null) {
        map.set(Number(item.id), item.name)
      }
    })
    return map
  }, [map_data.dzone])

  const findProduct = (id) => productLookup.get(Number(id))

  const findMix = (orderId, productId) => {
    const product = findProduct(productId)
    if (!product?.id) return null
    return mixLookup.get(`${Number(orderId)}_${Number(product.id)}`) || null
  }

  const computeRowTonnes = (dtRow, productId) => {
    const found = findMix(dtRow.id, productId)
    const mixPercentage = found ? Number(found.mix_percentage || 0) : 0
    return (mixPercentage / 100) * Number(dtRow.tons_ordered || 0)
  }

  const computeRowTonnesActual = (dtRow, productId) => {
    const found = findMix(dtRow.id, productId)
    const mixPercentage = found ? Number(found.mix_percentage || 0) : 0
    return (mixPercentage / 100) * Number(dtRow.tons_actual || 0)
  }

  const computeRowCompleted = (dtRow, productId) => {
    const found = findMix(dtRow.id, productId)
    const mixPercentage = found ? Number(found.mix_percentage || 0) : 0

    if (Number(dtRow.order_status) === 1) {
      return (mixPercentage / 100) * Number(dtRow.tons_actual || 0)
    }

    return (mixPercentage / 100) * Number(dtRow.tons_ordered || 0)
  }

  const getDataReport = (data) => {
    setDateRange(data)

    if (!data) {
      setError('')
      setPDFDate(null)
      setMapData({})
      setAdditionalLineItems([])
      setLoadingDate(false)
      return
    }

    const spanDays = getRangeDays(data)

    if (spanDays > FETCH_MAX_DAYS) {
      setError(
        `Selected range is too large (${spanDays} days). Please choose ${FETCH_MAX_DAYS} days or less.`,
      )
      setPDFDate(null)
      setMapData({})
      setAdditionalLineItems([])
      setLoadingDate(false)
      return
    }

    setLoadingDate(true)
    setError('')

    axios
      .post('/api/get_custom_report', { data })
      .then((res) => {
        const spreadreport = res?.data?.spreadreport || {}

        console.log('Custom report loaded')
        console.log('Zones:', Object.keys(spreadreport).length)
        console.log('Products:', res?.data?.product?.length || 0)
        console.log('Order list products:', res?.data?.order_list_products?.length || 0)

        if (!spreadreport || Object.keys(spreadreport).length === 0) {
          setError('No data found')
          setPDFDate(null)
          setAdditionalLineItems([])
          setLoadingDate(false)
          return
        }

        setMapData({
          dzone: res.data.dzone || [],
          otype: res.data.otype || [],
          machine: res.data.machine || [],
          product: res.data.product || [],
          order_list_products: res.data.order_list_products || [],
          sku_location: res.data.sku_location || res.data.skulocation || res.data.skuLocation || [],
        })

        setPDFDate(spreadreport)

        const orderIds = new Set()

        Object.keys(spreadreport).forEach((zoneId) => {
          Object.keys(spreadreport[zoneId] || {}).forEach((prodId) => {
            ;(spreadreport[zoneId][prodId] || []).forEach((row) => {
              if (row?.id) {
                orderIds.add(Number(row.id))
              }
            })
          })
        })

        const collected = []

        if (res.data.orders) {
          const traverse = (node) => {
            if (!node) return

            if (Array.isArray(node)) {
              node.forEach((o) => {
                if (!o?.id) return

                const items = Array.isArray(o.other_line_items)
                  ? o.other_line_items
                  : Array.isArray(o.line_items)
                  ? o.line_items
                  : null

                if (items && orderIds.has(Number(o.id))) {
                  collected.push(...items)
                }
              })
            } else if (typeof node === 'object') {
              Object.values(node).forEach((v) => traverse(v))
            }
          }

          traverse(res.data.orders)
        }

        setAdditionalLineItems(collected)
        setLoadingDate(false)
      })
      .catch((err) => {
        console.log('API ERROR:', err)
        setError('Error fetching report data')
        setPDFDate(null)
        setAdditionalLineItems([])
        setLoadingDate(false)
      })
  }

  const formatDate = (date) => {
    const d = new Date(date)
    let month = `${d.getMonth() + 1}`
    let day = `${d.getDate()}`
    const year = d.getFullYear()

    if (month.length < 2) month = `0${month}`
    if (day.length < 2) day = `0${day}`

    return [year, month, day].join('-')
  }

  const formatDateRange = (range) => {
    if (!range || !Array.isArray(range)) return ''
    const [start, end] = range
    return `${formatDate(start)} to ${formatDate(end)}`
  }

  const downloadExcel = async () => {
    if (!pdfData) return

    setExcelPreparing(true)

    try {
      const spanDays = getRangeDays(dateRange)

      if (spanDays > FETCH_MAX_DAYS) {
        setError(`Excel export is limited to ${FETCH_MAX_DAYS} days.`)
        return
      }

      await new Promise((res) => setTimeout(res, 50))

      let skuList = map_data.sku_location || map_data.skulocation || map_data.skuLocation || []

      if (!Array.isArray(skuList) || skuList.length === 0) {
        try {
          const resp = await getSkuLocation()
          skuList =
            resp?.data?.sku_location ||
            resp?.data?.skulocation ||
            resp?.data?.skuLocation ||
            resp?.data ||
            []

          setMapData((prev) => ({
            ...prev,
            sku_location: skuList,
          }))
        } catch (e) {
          console.warn('getSkuLocation() failed', e)
          skuList = []
        }
      }

      let customerMap = {}
      let xeroMap = {}

      try {
        const custResp = await getCustomers()
        const localCustomers = custResp?.data?.customer || []
        const xeroContacts =
          custResp?.data?.xeroContact?.body?.Contacts || custResp?.data?.xero_contacts || []

        localCustomers.forEach((c) => {
          if (!c) return
          if (c.id != null) customerMap[String(c.id)] = c
          if (c.contact_id != null) customerMap[String(c.contact_id)] = c
        })

        xeroContacts.forEach((xc) => {
          if (!xc) return
          const key = String(xc.ContactID ?? xc.contact_id ?? '')
          if (key) {
            xeroMap[key] = xc.AccountNumber ?? xc.AccountNo ?? xc.Account ?? null
          }
        })
      } catch (e) {
        console.warn('getCustomers() failed', e)
      }

      const combined = []

      if (Array.isArray(additionalLineItems) && additionalLineItems.length) {
        combined.push(...additionalLineItems)
      }

      Object.keys(pdfData || {}).forEach((zoneId) => {
        Object.keys(pdfData[zoneId] || {}).forEach((productId) => {
          ;(pdfData[zoneId][productId] || []).forEach((order) => {
            if (!order) return

            const items = Array.isArray(order.other_line_items)
              ? order.other_line_items
              : Array.isArray(order.line_items)
              ? order.line_items
              : null

            if (items?.length) {
              items.forEach((it) => {
                if (!it) return
                const newItem = { ...it }
                if (newItem.order_id == null) {
                  newItem.order_id = order.id ?? order.order_id ?? order.order
                }
                combined.push(newItem)
              })
            }
          })
        })
      })

      const seen = new Set()
      const deduped = []

      combined.forEach((it) => {
        if (!it) return

        const orderId = it.order_id ?? it.orderId ?? it.order ?? ''
        const description = it.description ?? it.desc ?? it.name ?? ''
        const quantity = Number(it.quantity ?? it.qty ?? it.Qty ?? 0)
        const amount = Number(it.amount ?? it.Amount ?? 0)
        const key = `${orderId}||${description}||${quantity}||${amount}`

        if (!seen.has(key)) {
          seen.add(key)
          deduped.push({
            ...it,
            quantity,
            amount,
            order_id: orderId,
          })
        }
      })

      const headers = [
        'Zone',
        'Delivery Address',
        'Product',
        'Customer Name',
        'Account Contact Name',
        'Customer Code',
        'Spread Date',
        'Order Date',
        'Tonnes Ordered',
        'Tonnes Actual',
        'Predicted',
      ]

      const aoa = [headers]
      let grandOrdered = 0
      let grandActual = 0
      let grandPred = 0

      Object.keys(pdfData || {}).forEach((zoneId) => {
        const zoneName = zoneLookup.get(Number(zoneId)) || zoneId

        Object.keys(pdfData[zoneId] || {}).forEach((productId) => {
          const productObj = findProduct(productId)
          const productLabel =
            productObj?.product_docket || productObj?.product_name || `Product ${productId}`
          const list = pdfData[zoneId][productId] || []

          list.forEach((dtRow) => {
            const ordered = computeRowTonnes(dtRow, productId)
            const actual = computeRowTonnesActual(dtRow, productId)
            const pred = computeRowCompleted(dtRow, productId)

            grandOrdered += ordered
            grandActual += actual
            grandPred += pred

            let deliveryAddress = ''
            const custAdd = dtRow.customer?.customer_address

            if (custAdd) {
              const addr = Array.isArray(custAdd) ? custAdd[0] : custAdd
              const parts = [addr?.address, addr?.street, addr?.city, addr?.state, addr?.post_code]
              deliveryAddress = parts.filter(Boolean).join(', ')
            } else if (dtRow.order_delivery_address) {
              deliveryAddress = dtRow.order_delivery_address
            }

            const cust = dtRow.customer || {}
            const custId = cust?.id ?? cust?.customer_id ?? cust?.account_id
            const contactIdKey = String(
              cust?.contact_id ?? cust?.ContactID ?? customerMap[String(custId)]?.contact_id ?? '',
            )

            const localCode =
              cust?.code ?? customerMap[String(custId)]?.code ?? customerMap[contactIdKey]?.code

            const xeroId = localCode ?? xeroMap[contactIdKey] ?? ''

            aoa.push([
              zoneName,
              deliveryAddress,
              productLabel,
              cust?.account_name || '',
              cust?.account_contact_name || '',
              xeroId,
              dtRow.tons_complete_time || '',
              formatDate(dtRow.created_at),
              Number(ordered.toFixed(3)),
              Number(actual.toFixed(3)),
              Number(pred.toFixed(3)),
            ])
          })
        })
      })

      aoa.push(['', '', '', '', '', '', '', '', '', '', ''])
      const grandIndex = aoa.length
      aoa.push([
        '',
        '',
        'GRAND TOTAL',
        '',
        '',
        '',
        '',
        '',
        Number(grandOrdered.toFixed(3)),
        Number(grandActual.toFixed(3)),
        Number(grandPred.toFixed(3)),
      ])

      const ws = XLSX.utils.aoa_to_sheet(aoa)

      ws['!cols'] = [
        { wpx: 150 },
        { wpx: 300 },
        { wpx: 200 },
        { wpx: 300 },
        { wpx: 150 },
        { wpx: 150 },
        { wpx: 140 },
        { wpx: 120 },
        { wpx: 120 },
        { wpx: 120 },
        { wpx: 120 },
      ]

      headers.forEach((_, c) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c })
        if (!ws[addr]) ws[addr] = { t: 's', v: headers[c] }
        ws[addr].s = { font: { bold: true } }
      })

      for (let c = 0; c < headers.length; c++) {
        const addr = XLSX.utils.encode_cell({ r: grandIndex, c })
        if (ws[addr]) {
          ws[addr].s = {
            font: { bold: true },
            alignment: { horizontal: 'right' },
          }
        }
      }

      const numericCols = [8, 9, 10]
      for (let r = 1; r <= aoa.length - 1; r++) {
        numericCols.forEach((c) => {
          const addr = XLSX.utils.encode_cell({ r, c })
          if (ws[addr]) {
            ws[addr].t = 'n'
            ws[addr].s = {
              ...(ws[addr].s || {}),
              alignment: { horizontal: 'right' },
            }
          }
        })
      }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Spread Report (DA)')

      if (deduped.length > 0) {
        const headers2 = [
          'Order ID',
          'Line Item Description',
          'Location',
          'Division',
          'Quantity',
          'Amount',
          'Total',
        ]

        const aoa2 = [headers2]

        deduped.forEach((it) => {
          const orderId = it.order_id ?? it.orderId ?? it.order ?? ''
          const description = it.description ?? it.desc ?? it.name ?? ''

          let locationVal = it.location ?? it.sku_location ?? it.location_name ?? ''

          if (
            locationVal !== '' &&
            (typeof locationVal === 'number' || /^\d+$/.test(String(locationVal)))
          ) {
            const lid = Number(locationVal)
            const foundLoc = Array.isArray(skuList)
              ? skuList.find((l) => Number(l.id) === lid)
              : null

            locationVal = foundLoc?.name || String(lid)
          }

          let divisionVal = it.division ?? it.branch ?? it.division_name ?? ''
          if (
            divisionVal !== '' &&
            (typeof divisionVal === 'number' || /^\d+$/.test(String(divisionVal)))
          ) {
            const did = Number(divisionVal)
            divisionVal =
              did === 1 ? 'Spreading' : did === 2 ? 'Transport' : did === 3 ? 'Mining' : String(did)
          }

          const quantity = Number(it.quantity ?? it.qty ?? it.Qty ?? 0)
          const amount = Number(it.amount ?? it.Amount ?? 0)
          const total = Number(it.total ?? it.Total ?? quantity * amount)

          aoa2.push([orderId, description, locationVal, divisionVal, quantity, amount, total])
        })

        const ws2 = XLSX.utils.aoa_to_sheet(aoa2)

        headers2.forEach((_, c) => {
          const addr = XLSX.utils.encode_cell({ r: 0, c })
          if (ws2[addr]) ws2[addr].s = { font: { bold: true } }
        })

        ws2['!cols'] = [
          { wpx: 100 },
          { wpx: 400 },
          { wpx: 200 },
          { wpx: 150 },
          { wpx: 100 },
          { wpx: 120 },
          { wpx: 140 },
        ]

        XLSX.utils.book_append_sheet(wb, ws2, 'Additional Line Items')
      }

      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      })

      const blob = new Blob([wbout], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `spread_report_DA_${formatDateRange(dateRange)}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      setExcelPreparing(false)
    }
  }

  return (
    <>
      <h1 className="mb-4">Spread Report (DA)</h1>

      <div className="d-flex align-items-end">
        <div className="d-flex align-items-center">
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            style={{ width: 230 }}
            onChange={getDataReport}
            value={dateRange}
          />
        </div>

        <div className="d-flex align-items-center mx-3">
          {error ? (
            <div className="btn btn-block btn-outline-info btn-sm">{error}</div>
          ) : dateRange === null ? null : loadingDate ? (
            <div className="btn btn-block btn-outline-info btn-sm">Loading...</div>
          ) : pdfData && !loadingDate ? (
            <>
              {!isLargeRangeForPdf ? (
                <BlobProvider
                  document={
                    <Spread
                      item={pdfData}
                      map_data={map_data}
                      dateRange={dateRange}
                      spread_report_open={true}
                    />
                  }
                >
                  {({ url, loading }) =>
                    !loading ? (
                      <a
                        download={`spread_report_${formatDateRange(dateRange)}.pdf`}
                        href={url}
                        className="btn btn-block btn-outline-info btn-sm"
                        rel="noreferrer"
                        target="_blank"
                      >
                        Download Report
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-block btn-outline-info btn-sm"
                        disabled
                      >
                        Preparing Report
                      </button>
                    )
                  }
                </BlobProvider>
              ) : (
                <button
                  type="button"
                  className="btn btn-block btn-outline-secondary btn-sm"
                  disabled
                >
                  PDF disabled for large range
                </button>
              )}

              <div style={{ display: 'inline-block', marginLeft: 8 }}>
                {excelPreparing ? (
                  <button
                    type="button"
                    className="btn btn-block btn-outline-success btn-sm"
                    disabled
                  >
                    Preparing Excel...
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-block btn-outline-success btn-sm"
                    onClick={downloadExcel}
                  >
                    Download Excel
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
      {selectedDays > 0 && (
        <small className="mr-2 mt-2 text-muted">
          Selected range: {selectedDays} day(s) | Max 1000 days
        </small>
      )}
    </>
  )
}

export default SpreadReport
